import type { GeoCoordinates } from '../geoCache';

/** True when Maps JavaScript API (incl. Geocoder) is loaded in the browser. */
export function isGoogleMapsGeocoderReady(): boolean {
  if (typeof window === 'undefined') return false;
  const g = (window as unknown as { google?: { maps?: { Geocoder?: unknown } } }).google;
  return Boolean(g?.maps?.Geocoder);
}

/**
 * The Maps JS Geocoder reports results only through its callback, and when the Maps key is rejected
 * (InvalidKeyMapError) that callback never fires. Without a cap, every caller awaiting a geocode —
 * e.g. syncPublicProfile during a certification save — would hang forever.
 */
const MAPS_JS_GEOCODE_TIMEOUT_MS = 8000;

/**
 * Geocode city/state via Maps JavaScript API Geocoder.
 * Works with HTTP-referrer–restricted browser keys (unlike REST Geocoding API).
 * Resolves null on failure or after MAPS_JS_GEOCODE_TIMEOUT_MS, so callers can fall back to REST.
 */
export function geocodeCityStateWithMapsJs(
  city: string,
  state: string
): Promise<GeoCoordinates | null> {
  return new Promise((resolve) => {
    if (!isGoogleMapsGeocoderReady()) {
      resolve(null);
      return;
    }

    const g = (window as unknown as { google: typeof google }).google;
    const geocoder = new g.maps.Geocoder();
    const address = state ? `${city}, ${state}, USA` : `${city}, USA`;

    let settled = false;
    const finish = (value: GeoCoordinates | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => {
      console.warn('[mapsJsGeocoder] Geocode timed out (Maps key rejected or network stalled):', address);
      finish(null);
    }, MAPS_JS_GEOCODE_TIMEOUT_MS);

    try {
      geocoder.geocode({ address, region: 'us' }, (results, status) => {
        if (status !== g.maps.GeocoderStatus.OK || !results?.[0]?.geometry?.location) {
          if (import.meta.env.DEV) {
            console.warn('[mapsJsGeocoder] Geocode failed:', address, status);
          }
          finish(null);
          return;
        }
        const loc = results[0].geometry.location;
        finish({ lat: loc.lat(), lng: loc.lng() });
      });
    } catch (err) {
      console.warn('[mapsJsGeocoder] Geocoder threw:', err);
      finish(null);
    }
  });
}
